/**
 * The canvas under the table: mounts a `TableScene` once, keeps it the size of
 * its frame, and hands it every new input. Hover and drag go straight to the
 * scene through `sceneRef`, because they change a frame, not the table.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { TableScene, type SceneInput } from './scene';

export interface TableCanvasProps {
  input: SceneInput | null;
  sceneRef: RefObject<TableScene | null>;
}

export function TableCanvas({ input, sceneRef }: TableCanvasProps) {
  const host = useRef<HTMLDivElement>(null);
  const latest = useRef(input);
  latest.current = input;

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const scene = new TableScene();
    let alive = true;
    const size = () => ({ w: el.clientWidth, h: el.clientHeight });
    const observer = new ResizeObserver(() => {
      const { w, h } = size();
      scene.resize(w, h);
    });
    const { w, h } = size();
    void scene.mount(el, w, h).then(() => {
      if (!alive) return;
      sceneRef.current = scene;
      // In development the scene is reachable from the console, to take a snapshot.
      if (import.meta.env.DEV) (window as unknown as { __table?: TableScene }).__table = scene;
      observer.observe(el);
      const now = size();
      scene.resize(now.w, now.h);
      if (latest.current) scene.update(latest.current);
    });
    return () => {
      alive = false;
      observer.disconnect();
      sceneRef.current = null;
      scene.destroy();
    };
  }, [sceneRef]);

  useEffect(() => {
    if (input) sceneRef.current?.update(input);
  }, [input, sceneRef]);

  return <div ref={host} className="table-canvas-host" />;
}
